import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../hooks/queries/useAuth';
import { hasExceededProjectLimit } from '../../lib/project-limits';
import { AlertTriangle } from 'lucide-react';
import { logger } from '../../utils/logger';

/**
 * Banner that displays when a user has exceeded their subscription limits
 * Shows warning with option to upgrade subscription
 */
export function SubscriptionLimitBanner() {
  const [bannerState, setBannerState] = useState<{
    visible: boolean;
    projectCount: number;
    projectLimit: number | 'unlimited';
    loading: boolean;
  }>({
    visible: false,
    projectCount: 0,
    projectLimit: 0,
    loading: true,
  });

  const { data: user } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const checkLimits = async () => {
      if (!user) {
        if (isMounted) {
          setBannerState(prev => ({ ...prev, visible: false, loading: false }));
        }
        return;
      }

      try {
        const { limitExceeded, projectCount, allowedProjects } = await hasExceededProjectLimit(
          user.id
        );

        if (isMounted) {
          setBannerState({
            visible: limitExceeded,
            projectCount,
            projectLimit: allowedProjects,
            loading: false,
          });
        }
      } catch (error) {
        logger.error('Error checking subscription limits:', error);
        if (isMounted) {
          setBannerState(prev => ({ ...prev, visible: false, loading: false }));
        }
      }
    };

    checkLimits();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [user]);

  const handleUpgradeClick = () => {
    navigate('/subscription', {
      state: {
        reason: 'project_limit_exceeded',
        message: `You have ${bannerState.projectCount} projects, but your current plan only allows ${bannerState.projectLimit}.`,
      },
    });
  };

  // If the banner shouldn't be shown, don't render anything
  if (bannerState.loading || !bannerState.visible) {
    return null;
  }
  
  // Only render when visible to prevent layout issues
  return (
    <div
      className="transition-all duration-300 ease-in-out overflow-hidden max-h-24 opacity-100 bg-red-500/20 border-t border-b border-red-600 text-red-100 px-4 py-3 mb-2"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-center justify-between flex-wrap">
        <div className="flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0" aria-hidden="true" />
          <p className="text-sm">
            <strong>Subscription Limit Exceeded:</strong> You have {bannerState.projectCount}{' '}
            projects, but your current plan only allows {bannerState.projectLimit}. Some projects
            have limited access.
          </p>
        </div>
        <button
          onClick={handleUpgradeClick}
          className="bg-red-700 hover:bg-red-800 text-white text-xs font-semibold py-1 px-3 rounded mt-2 sm:mt-0 transition-colors"
          aria-label="Upgrade subscription plan"
        >
          Upgrade Plan
        </button>
      </div>
    </div>
  );
}
